'use client';

import { useMemo, useEffect, useState, useRef } from 'react';
import gsap from 'gsap/all';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation, Container, Text } from '@weareconceptstudio/core';

//* Libs
import { getPosts } from '@/libs/get-data';

//* Style
import BlogIndividualTemplateStyle from './style';

//* Component
import Hero from '../components/Hero';
import Description from '../components/Description';
import BlogItem from '../../components/List/Item';
import Share from '@/components/Share';
import CallToAction from '@/modules/_sections/CallToAction';
import TitleAnim from '@/modules/_animation/Title';

gsap.registerPlugin(ScrollTrigger);

const BlogIndividualTemplate = ({ pageData }) => {
	const { selectedLang } = useTranslation();

	//! State
	const [data, setData] = useState([]);
	const currentPostId = pageData.id;

	//! Load Image
	const [imageLoad, setImageLoad] = useState(false);

	//! Ref
	const wrapperBlogs = useRef();
	const triggerBlogs = useRef();
	const shareWrapperRef = useRef();
	const triggerShareMobile = useRef();

	useEffect(() => {
		getPosts({ currentPostId, lang: selectedLang, params: { latest: true } }).then((res) => setData(res?.posts || []));
	}, [selectedLang, currentPostId]);

	//! News Item
	const dataPosts = useMemo(() => {
		return data?.map((item, i) => (
			<div
				key={i}
				className={'item-blog opacity-zero'}
			>
				<BlogItem {...item} />
			</div>
		));
	}, [data]);

	useEffect(() => {
		if (wrapperBlogs.current) {
			const children = wrapperBlogs.current.children;

			triggerBlogs.current = ScrollTrigger.create({
				start: 'top bottom',
				end: 'top top',
				trigger: wrapperBlogs.current,
				// markers: true,
				onEnter: () => {
					if (children.length) {
						gsap.fromTo(
							children,
							{
								opacity: 0,
							},
							{
								opacity: 1,
								ease: 'power2.out',
								duration: 0.9,
								stagger: 0.05,
							}
						);
						gsap.fromTo(
							children,
							{
								y: (arg) => {
									return `${(arg + 1) * 30}%`;
								},
							},
							{
								y: 0,
								ease: 'power2.out',
								duration: 0.6,
								stagger: 0.05,
							}
						);
					}
				},
				onLeaveBack: () => {
					if (children.length) {
						gsap.set(children, {
							y: (arg) => {
								return `${(arg + 1) * 30}%`;
							},
							opacity: 0,
						});
					}
				},
			});

			const shareItem = shareWrapperRef.current.querySelectorAll('a');
			triggerShareMobile.current = ScrollTrigger.create({
				start: 'top bottom',
				end: 'top top',
				trigger: shareWrapperRef.current,
				// markers: true,
				onEnter: () => {
					if (shareItem.length) {
						gsap.fromTo(shareItem, { scale: 0 }, { scale: 1, ease: 'power2.inOut', duration: 0.6, stagger: 0.07 });
					}
				},
				onLeaveBack: () => {
					if (shareItem.length) {
						gsap.set(shareItem, { scale: 0 });
					}
				},
			});
		}
		return () => {
			if (triggerBlogs.current) {
				triggerBlogs.current.kill();
			}
			if (triggerShareMobile.current) {
				triggerShareMobile.current.kill();
			}
		};
	});

	return (
		<BlogIndividualTemplateStyle>
			<Hero
				imageLoad={imageLoad}
				setImageLoad={setImageLoad}
				image_mobile={pageData.mobile_image}
				image_main={pageData.desktop_image}
				date={pageData.date}
				title={pageData.title}
			/>

			<Container
				isSection
				row
				className={'description-section'}
			>
				<div className='col-1 col-ds-12' />
				<div className={'col-8 col-ds-10 col-m-12'}>
					<div className='description-wrap'>
						<Description
							imageLoad={imageLoad}
							setImageLoad={setImageLoad}
							items={pageData.descriptions}
						/>
					</div>
				</div>

				<div
					ref={shareWrapperRef}
					className={'col-m-12 share-bottom'}
				>
					<Share />
				</div>

				<div className='col-3 col-ds-2 col-m-12' />

				<div className={'col-1 col-ds-12'} />

				<div className={'col-10 col-ds-12 col-m-12'}>
					{dataPosts?.length > 0 && (
						<>
							<div className='wrapper-related-titles'>
								<TitleAnim
									load={true}
									trigger={'bottom'}
								>
									<Text
										text={'lastBlogs'}
										className={'font-larken-light related-text h2'}
									/>
								</TitleAnim>
							</div>

							<div className={'col-2 col-t-1 col-m-12'} />

							<div className='wrapper-items'>
								<div
									ref={wrapperBlogs}
									className={'wrapper-blogs'}
								>
									{dataPosts}
								</div>
							</div>

							<div className={'col-2 col-t-1 col-m-12'} />
						</>
					)}
				</div>

				<div className={'col-1 col-ds-12'} />
			</Container>

			<CallToAction />
		</BlogIndividualTemplateStyle>
	);
};

export default BlogIndividualTemplate;
